import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Stack } from '@mui/material';
import { useAuth } from './useAuth';
import type { AuthContextValue, Scope } from './AuthContext';
import type { ReactNode } from 'react';
import { paths } from '@/router/paths';

interface ProtectedRouteProps {
  children: ReactNode;
  /** If set, route requires AT LEAST ONE of these scopes. */
  requireAnyScope?: Scope[];
}

/**
 * Wrap a route element to require an authenticated session and (optionally) a
 * scope. Sends unauth'd users to the login screen with a {@code from} query
 * so they bounce back after logging in.
 *
 * While AuthContext is still resolving the initial user (async check against
 * the OIDC user store), renders a spinner instead of bouncing to /login —
 * otherwise a valid session gets briefly kicked out on every page load.
 */
export function ProtectedRoute({ children, requireAnyScope }: ProtectedRouteProps) {
  const { session, loading, hasScope }: AuthContextValue = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '100vh' }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (!session) {
    return <Navigate to={paths.login} state={{ from: location.pathname }} replace />;
  }
  if (requireAnyScope && !requireAnyScope.some(hasScope)) {
    return <Navigate to={paths.forbidden} replace />;
  }
  return <>{children}</>;
}
